<template>
  <div class="withdraw-form">
    <div
      v-for="(item, index) in renderItems"
      :key="item.depth + '-' + item.key + '-' + index"
      class="form-item"
      :class="{ 'form-item--child': item.depth > 0 }"
    >
      <div class="form-label">
        {{ item.title }}
        <span v-if="item.required" class="required">*</span>
      </div>
      <div class="form-input">
        <van-field
          v-if="item.type === 'select'"
          :value="form[item.key]"
          readonly
          :placeholder="item.placeholder || $t('withdraw.select_placeholder')"
          @click="openPicker(item)"
        >
          <template #right-icon>
            <van-icon name="arrow-down" class="select-arrow" />
          </template>
        </van-field>
        <van-field
          v-else
          :value="form[item.key]"
          :placeholder="item.placeholder || item.title"
          @input="onInput(item.key, $event)"
        />
      </div>
    </div>

    <van-popup v-model="showPicker" round position="bottom" class="withdraw-select-popup">
      <div class="select-panel">
        <div class="select-header">
          <div class="select-cancel" role="button" tabindex="0" @click="showPicker = false">{{ $t('setting.cancel') }}</div>
          <div class="select-title">{{ pickerTitle }}</div>
          <div class="select-cancel select-cancel--ghost"></div>
        </div>
        <div class="select-list">
          <div
            v-for="(option, index) in pickerOptions"
            :key="option.key !== undefined ? option.key : index"
            class="select-option"
            :class="{ 'select-option--active': isSelected(option) }"
            role="button"
            tabindex="0"
            :aria-label="option.title"
            @click="selectOption(option)"
            @keyup.enter="selectOption(option)"
          >
            <span class="select-option-text">{{ option.title }}</span>
            <van-icon v-if="isSelected(option)" name="success" class="select-check" />
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { flattenVisibleFields, findOption, isSameToken } from '@/common/withdrawForm';

export default {
  name: 'WithdrawInfoForm',
  props: {
    fields: {
      type: Array,
      default: () => []
    },
    form: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      showPicker: false,
      pickerField: null,
      pickerTitle: '',
      pickerOptions: []
    };
  },
  computed: {
    renderItems() {
      return flattenVisibleFields(this.fields, this.form);
    }
  },
  methods: {
    onInput(key, value) {
      this.$set(this.form, key, value);
    },
    openPicker(field) {
      const options = Array.isArray(field.options) ? field.options : [];
      if (!options.length) {
        this.$toast(this.$t('withdraw.no_options'));
        return;
      }
      this.pickerField = field;
      this.pickerTitle = field.title;
      this.pickerOptions = options;
      this.showPicker = true;
    },
    isSelected(option) {
      if (!this.pickerField || !option) {
        return false;
      }
      const value = this.form[this.pickerField.key];
      return isSameToken(option.title, value) || (option.key !== undefined && isSameToken(option.key, value));
    },
    clearValues(fields) {
      (fields || []).forEach(field => {
        if (!field || !field.key) {
          return;
        }
        this.$set(this.form, field.key, '');
        if (field.type === 'select') {
          (field.options || []).forEach(option => {
            if (option && Array.isArray(option.children)) {
              this.clearValues(option.children);
            }
          });
        }
      });
    },
    selectOption(option) {
      const field = this.pickerField;
      if (!field || !option) {
        this.showPicker = false;
        return;
      }
      const previous = findOption(field, this.form[field.key]);
      if (previous && previous !== option && Array.isArray(previous.children)) {
        this.clearValues(previous.children);
      }
      this.$set(this.form, field.key, option.title);
      this.showPicker = false;
    }
  }
};
</script>

<style lang="less" scoped>
.withdraw-form {
  width: 100%;
}

.form-item {
  margin-top: 4.5%;

  .form-label {
    color: #4a3a5c;
    font-size: 0.88rem;
  }

  .form-input {
    background: #fff;
    border-radius: 0.9rem;
    border: 1px solid #f0e2f2;
    height: 3rem;
    margin-top: 3%;
    display: flex;
    align-items: center;

    /deep/ .van-field {
      background-color: transparent;
      padding: 0 1.25rem;
      color: #4a3a5c;
      font-size: 0.88rem;
      display: flex;
      align-items: center;
      border: none;
      height: 1.5rem;

      &::after {
        display: none;
      }

      /deep/ .van-field__control {
        color: #323233;
        background-color: transparent;
        font-size: 0.88rem;
      }

      /deep/ .van-field__body {
        height: 1.5rem;
      }

      /deep/ .van-field__control::-webkit-input-placeholder {
        color: #969799;
      }
    }
  }

  .select-arrow {
    color: #969799;
  }

  .required {
    color: #f53d81;
    margin-left: 0.15rem;
  }
}

.form-item--child {
  width: 100%;
  margin-left: 0;
}

.withdraw-select-popup {
  overflow: hidden;
}

.select-panel {
  display: flex;
  flex-direction: column;
  max-height: 60vh;
}

.select-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: 3rem;
  padding: 0 0.75rem;
  border-bottom: 1px solid #f0e2f2;
}

.select-cancel {
  width: 4rem;
  font-size: 0.9rem;
  color: #969799;
  cursor: pointer;
}

.select-cancel--ghost {
  visibility: hidden;
}

.select-title {
  flex: 1;
  overflow: hidden;
  color: #4a3a5c;
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-list {
  overflow-y: auto;
  padding: 0.25rem 0;
  -webkit-overflow-scrolling: touch;
}

.select-option {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem 1.25rem;
  color: #4a3a5c;
  font-size: 0.95rem;
  border-bottom: 1px solid #faf5fb;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &:active {
    background: #fdf5fa;
  }
}

.select-option--active {
  color: #c45b86;
  font-weight: 500;
}

.select-check {
  position: absolute;
  right: 1.25rem;
  color: #c45b86;
}
</style>
